/* eslint-disable max-lines-per-function */
import React, { useEffect, Suspense, useCallback } from 'react';

import { BannerApp } from '../sections/BannerApp';
import { MainFooter } from '../sections/MainFooter/MainFooter';

import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { SizingToolState, ModalToggleState, DrawerToggleState } from '../models/actionStates';
import { ViewProduct } from '../models/ViewProduct.model';
import { getHashVariable, setCookie, getCookie, poll, showMarketingMessage, getSwimlane, clearHashVariable } from '../util';
import { HoneyloveWindow } from '../models/HoneyloveWindow';
import { ProductHandle } from '../enums/ProductHandle.enum';
import { Portal } from './components/Portal';
import { SelectionType } from '../enums/SelectionType.enum';
import ModalSingletonApp from '../modal_singleton/ModalSingletonApp';
import DrawerSingletonApp from '../drawer_singleton/DrawerSingletonApp';
import SizingToolApp from '../sizing-tool/SizingToolApp';

import { abTestState, deviceTypeState, environmentContextState, marketingPromoState, pageContextState, shopifyState } from '../recoil/atoms';
import { trackProductDetailViewed, trackReactPageLoaded, trackSwimlaneLoaded } from '../tracking/events';
import { HoneyloveAbTests } from '../models/HoneyloveAbTests';
import { useWindowDimensions } from '../hooks/useWindowDimensions';
import { DeviceType } from '../enums/Device.enum';
import { cartDrawerState, cartState, productsState, sizingToolState } from '../recoil/selectors';
import { PageContext } from '../enums/PageContext.enum';
import { useReverseModalState, useSetModalState } from '../hooks/actions/useModalState';
import { useClearCartAction, useGetCartAction } from '../hooks/actions/useCartState';
import { CartError } from './CartError';
import { useDiscountCodeDetection } from '../hooks/useDiscountCodeDetection';
import { useApplyDiscountCodeAction } from '../hooks/actions/useApplyDiscountCode';
import { ClerkLoaded, ClerkProvider, SignedIn, SignedOut } from '@clerk/clerk-react';
import LoyaltyApp from '../loyalty/LoyaltyApp';
import SignInApp from '../loyalty/SignInApp';
import { SetterOrUpdater } from 'recoil';
import { ModalState } from '../models/ModalState';
import { useSupportModal } from '../hooks/actions/useSupportModal';
import SignUpApp from '../loyalty/SignUpApp';
import MagicLinkVerificationApp from '../loyalty/MagicLinkVerificationApp';
import { LanderApp } from '../landing_pages/LanderApp';
import { ProductDetailLander } from '../landing_pages/ProductDetailLander/ProductDetailLander';
import { useProductSelection } from '../hooks/useProductSelection';
import { Fallback } from '../components/Fallback';
import { MainHeader } from '../sections/MainHeader/MainHeader';
import { SizingToolStateProps } from '../models/SizingToolState.model';

const LazyAffiliatesApp = React.lazy(() => import('../side_pages/AffiliatesApp' /* webpackChunkName: "affiliates_app" */));
const LazyHowToWearApp = React.lazy(() => import('../side_pages/HowToWearApp' /* webpackChunkName: "how_to_wear_app" */));
const LazyQuizApp = React.lazy(() => import('../quiz/QuizApp' /* webpackChunkName: "quiz_app" */));
const LazyVipApp = React.lazy(() => import('../side_pages/VipApp' /* webpackChunkName: "vip_app" */));
const LazyFourOhFourApp = React.lazy(() => import('../side_pages/FourOhFourApp' /* webpackChunkName: "four_oh_four_app" */));
const LazyAmazonApp = React.lazy(() => import('../side_pages/AmazonApp' /* webpackChunkName: "amazon_app" */));
const LazyNordstromApp = React.lazy(() => import('../side_pages/NordstromApp' /* webpackChunkName: "nordstrom_app" */));
const LazyReturnsApp = React.lazy(() => import('../returns/ReturnsApp' /* webpackChunkName: "returns_app" */));
const LazyReturnsPolicyApp = React.lazy(() => import('../returns/ReturnsPolicyApp' /* webpackChunkName: "returns_policy_app" */));
const LazyCouponsApp = React.lazy(() => import('../side_pages/CouponsApp' /* webpackChunkName: "coupon_app" */));
const LazyFrontlineWorkerApp = React.lazy(() => import('../side_pages/FrontlineWorkerApp' /* webpackChunkName: "frontline_worker_app" */));
const LazySmsApp = React.lazy(() => import('../side_pages/SmsApp' /* webpackChunkName: "sms_app" */));
const LazyConsultationApp = React.lazy(() => import('../side_pages/ConsultationApp' /* webpackChunkName: "consultation_app" */));
const LazyTrackYourOrderApp = React.lazy(() => import('../side_pages/TrackYourOrderApp' /* webpackChunkName: "track_your_order_app" */));
const LazyCollectionApp = React.lazy(() => import('../collection/CollectionApp' /* webpackChunkName: "collection_app" */));
const LazyBlogIndexApp = React.lazy(() => import('../blog/BlogIndexApp' /* webpackChunkName: "blog_index_app" */));
const LazyBlogArticleApp = React.lazy(() => import('../blog/BlogArticleApp' /* webpackChunkName: "blog_article_app" */));
const LazyReviewsPageApp = React.lazy(() => import('../side_pages/ReviewsPageApp' /* webpackChunkName: "reviews_page_app" */));
const LazyAboutApp = React.lazy(() => import('../side_pages/AboutApp' /* webpackChunkName: "about_app" */));
const LazyPrivacyPolicyApp = React.lazy(() => import('../side_pages/PrivacyPolicyApp' /* webpackChunkName: "privacy_policy_app" */));
const LazyTermsOfUseApp = React.lazy(() => import('../side_pages/TermsOfUseApp' /* webpackChunkName: "terms_of_use_app" */));
const LazyAccessibilityApp = React.lazy(() => import('../side_pages/AccessibilityApp' /* webpackChunkName: "accessibility" */));
const LazyProfileApp = React.lazy(() => import('../profile/ProfileApp' /* webpackChunkName: "profile_app" */));

declare const window: HoneyloveWindow;

export const pageGlobalSpinnerStyles = {
  spinner: {
    position: 'absolute',
    opacity: 0.4,
    height: 50,
    width: 40,
    top: '33vh',
    left: 'calc(50% - 20px)',
  } as React.CSSProperties,
  img: {
    width: 64,
    height: 64,
  } as React.CSSProperties,
};

const RedirectToSignIn = () => {
  useEffect(() => {
    const signInURL = `/pages/sign-in`;
    window.location.href = signInURL;
  });
  return null;
};

const selectionType = SelectionType.ProductSection;
// eslint-disable-next-line max-lines-per-function
const App: React.FC = () => {
  const getCart = useGetCartAction();
  const [abTests, setAbTests] = useRecoilState(abTestState);
  const setMarketingPromo = useSetRecoilState(marketingPromoState);
  const setSizingToolState = useSetRecoilState(sizingToolState);
  const [deviceType, setDeviceType] = useRecoilState(deviceTypeState);
  const shopify = useRecoilValue(shopifyState);
  const products = useRecoilValue(productsState);
  const { product, variant, color, size, initializeHandle } = useProductSelection(selectionType);
  const pageContext = useRecoilValue(pageContextState);
  const clearCart = useClearCartAction();
  const { width } = useWindowDimensions();
  const setModal = useSetModalState();
  const reverseModal = useReverseModalState(true);
  const launchSupportModal = useSupportModal();
  const environmentContext = useRecoilValue(environmentContextState);
  const setCartDrawer = useSetRecoilState(cartDrawerState);

  useEffect(() => {
    watchPopState({ setModal, reverseModal, initializeHandle, setSizingToolState, product, products, launchSupportModal });
  }, [setModal, reverseModal, initializeHandle, setSizingToolState, product, products, launchSupportModal]);

  useDiscountCodeDetection();
  const applyDiscountCode = useApplyDiscountCodeAction();
  const cart = useRecoilValue(cartState);

  // open cart drawer if this is the cart page
  useEffect(() => {
    const isCartPage = document.location.pathname === '/cart';
    if (isCartPage) {
      setCartDrawer({
        toggleState: DrawerToggleState.Open,
      });
    }
  }, [setCartDrawer]);

  useEffect(() => {
    getCart();

    window.Honeylove = window.Honeylove || {};
    window.Honeylove.util = {
      setCookie,
      getCookie,
      poll,
      applyDiscountCode,
    };
    window.Honeylove.cart = window.Honeylove.cart || {};
    window.Honeylove.cart.clear = () => clearCart();
    window.Honeylove.actions = window.Honeylove.actions || {};
    window.Honeylove.actions.setMarketingPromo = setMarketingPromo;
    window.Honeylove.actions.showMarketingMessage = showMarketingMessage;
    window.Honeylove.abTests = window.Honeylove.abTests || {};
    window.Honeylove.abTests.set = (key: keyof HoneyloveAbTests, value = true) => {
      setAbTests({ ...abTests, [key]: value });
    };
    window.Honeylove.swimlane = window.Honeylove.swimlane || getSwimlane();

    if (pageContext === PageContext.ProductPage && shopify) {
      trackProductDetailViewed(shopify, product, variant, color, size);
    }
    trackReactPageLoaded(pageContext);
    trackSwimlaneLoaded(window.Honeylove.swimlane);

    if (getHashVariable('contact-us') === 'true') {
      launchSupportModal();
    }
  }, []); // don't add dependencies here or events will double fire

  const updateDeviceType = useCallback(
    (newDeviceType: DeviceType) => {
      if (deviceType !== newDeviceType) {
        setDeviceType(newDeviceType);
      }
    },
    [deviceType, setDeviceType],
  );

  useEffect(() => {
    const isMobile = width < 768;
    const isDesktop = width > 1024;
    const isTablet = !isMobile && !isDesktop;
    const newDeviceType = isMobile ? DeviceType.Mobile : isTablet ? DeviceType.Tablet : DeviceType.Desktop;
    updateDeviceType(newDeviceType);
  }, [width, updateDeviceType]);

  useEffect(() => {
    if (cart.error) {
      const modalContents = <CartError />;

      setSizingToolState({ sizingToolState: SizingToolState.Closed, selectionType: SelectionType.ProductSection, selectionKey: '0' });
      setModal({
        contents: modalContents,
        height: 'auto',
        width: 'auto',
        maxWidth: '100vw',
        maxHeight: '100vh',
        toggleState: ModalToggleState.Open,
        scrollToTop: true,
        ignoreLightBoxClick: true,
      });
    }
  }, [cart?.error]);

  return (
    <>
      <ClerkProvider frontendApi={`clerk.${environmentContext.clerkDomain}`}>
        {/* Clerk Components */}
        <Portal mountId="hl-primary-navigation">
          <MainHeader />
        </Portal>
        <Portal mountId="loyalty-page">
          <LoyaltyApp />
        </Portal>
        <ClerkLoaded>
          <Portal mountId="sign-in-page">
            <SignInApp />
          </Portal>
          <Portal mountId="sign-up-page">
            <SignUpApp />
          </Portal>
          <Portal mountId="magic-link-verification-page">
            <MagicLinkVerificationApp />
          </Portal>
          <Portal mountId="profile-page">
            <SignedIn>
              <Suspense fallback={<Fallback />}>
                <LazyProfileApp />
              </Suspense>
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </Portal>
        </ClerkLoaded>
      </ClerkProvider>
      {/* required on all pages */}
      <Portal mountId="banner-app">
        <BannerApp />
      </Portal>
      <Portal mountId="modal-singleton">
        <ModalSingletonApp />
      </Portal>
      <Portal mountId="drawer-singleton">
        <DrawerSingletonApp />
      </Portal>
      <SizingToolApp />
      {/* required on LDP, PDP */}
      <Portal mountId="product-detail">
        <ProductDetailLander />
      </Portal>
      <Portal mountId="lander">
        <Suspense fallback={<Fallback />}>
          <LanderApp />
        </Suspense>
      </Portal>
      <Portal mountId="product-lander">
        <Suspense fallback={<Fallback />}>
          <LanderApp mode={'PRODUCT'} />
        </Suspense>
      </Portal>
      <Portal mountId="hl-app-collection">
        <Suspense fallback={<Fallback />}>
          <LazyCollectionApp />
        </Suspense>
      </Portal>
      <Portal mountId="blog-index">
        <Suspense fallback={<Fallback />}>
          <LazyBlogIndexApp />
        </Suspense>
      </Portal>
      <Portal mountId="blog-article">
        <Suspense fallback={<Fallback />}>
          <LazyBlogArticleApp />
        </Suspense>
      </Portal>
      <Portal mountId="hl-reviews-page">
        <Suspense fallback={<Fallback />}>
          <LazyReviewsPageApp />
        </Suspense>
      </Portal>
      <Portal mountId="about-us-page">
        <Suspense fallback={<Fallback />}>
          <LazyAboutApp />
        </Suspense>
      </Portal>
      <Portal mountId="privacy-policy-page">
        <Suspense fallback={<Fallback />}>
          <LazyPrivacyPolicyApp />
        </Suspense>
      </Portal>
      <Portal mountId="terms-of-use-page">
        <Suspense fallback={<Fallback />}>
          <LazyTermsOfUseApp />
        </Suspense>
      </Portal>
      <Portal mountId="vip-page">
        <Suspense fallback={<Fallback />}>
          <LazyVipApp />
        </Suspense>
      </Portal>
      <Portal mountId="four-oh-four-page">
        <Suspense fallback={<Fallback />}>
          <LazyFourOhFourApp />
        </Suspense>
      </Portal>
      <Portal mountId="amazon-page">
        <Suspense fallback={<Fallback />}>
          <LazyAmazonApp />
        </Suspense>
      </Portal>
      <Portal mountId="nordstrom-page">
        <Suspense fallback={<Fallback />}>
          <LazyNordstromApp />
        </Suspense>
      </Portal>
      <Portal mountId="returns-page">
        <Suspense fallback={<Fallback />}>
          <LazyReturnsApp />
        </Suspense>
      </Portal>
      <Portal mountId="return-policy-page">
        <Suspense fallback={<Fallback />}>
          <LazyReturnsPolicyApp />
        </Suspense>
      </Portal>
      <Portal mountId="accessibility-page">
        <Suspense fallback={<Fallback />}>
          <LazyAccessibilityApp />
        </Suspense>
      </Portal>
      <Portal mountId="coupons-page">
        <Suspense fallback={<Fallback />}>
          <LazyCouponsApp />
        </Suspense>
      </Portal>
      <Portal mountId="sms-page">
        <Suspense fallback={<Fallback />}>
          <LazySmsApp />
        </Suspense>
      </Portal>
      <Portal mountId="affiliates-page">
        <Suspense fallback={<Fallback />}>
          <LazyAffiliatesApp />
        </Suspense>
      </Portal>
      <Portal mountId="how-to-wear-page">
        <Suspense fallback={<Fallback />}>
          <LazyHowToWearApp />
        </Suspense>
      </Portal>
      <Portal mountId="sculptwear-quiz-page">
        <Suspense fallback={<Fallback />}>
          <LazyQuizApp />
        </Suspense>
      </Portal>
      <Portal mountId="consultation-page">
        <Suspense fallback={<Fallback />}>
          <LazyConsultationApp />
        </Suspense>
      </Portal>
      <Portal mountId="track-your-order-portal">
        <Suspense fallback={<Fallback />}>
          <LazyTrackYourOrderApp />
        </Suspense>
      </Portal>
      <Portal mountId="hl-main-footer">
        <MainFooter />
      </Portal>
      <Portal mountId="frontline-worker-page">
        <Suspense fallback={<Fallback />}>
          <LazyFrontlineWorkerApp />
        </Suspense>
      </Portal>
    </>
  );
};

const watchPopState = ({
  setModal,
  reverseModal,
  initializeHandle,
  setSizingToolState,
  product,
  products,
  launchSupportModal,
}: {
  setModal: SetterOrUpdater<ModalState>;
  reverseModal: () => void;
  initializeHandle: SetterOrUpdater<ProductHandle>;
  setSizingToolState: (props: SizingToolStateProps) => void;
  product: ViewProduct;
  products: {
    [handle in ProductHandle]: ViewProduct;
  };
  launchSupportModal: () => void;
}) => {
  if (typeof window !== 'undefined') {
    window.onpopstate = () => {
      if (getHashVariable('contact-us') === 'true') {
        launchSupportModal();
        return true;
      }

      if (getHashVariable('sizing-tool-open') === 'true') {
        const pageContext = typeof window !== 'undefined' ? window?.Honeylove?.pageContext : undefined;
        clearHashVariable('sizing-tool-open');

        setSizingToolState({
          sizingToolState: SizingToolState.Open,
          selectionType: SelectionType.ProductSection,
          selectionKey: '0',
          product: pageContext === PageContext.ProductPage ? product : undefined,
          initialProduct: pageContext === PageContext.ProductPage ? product : undefined,
        });
      } else if (getHashVariable('sizing-tool-open') === 'false') {
        clearHashVariable('sizing-tool-open');
        setSizingToolState({ sizingToolState: SizingToolState.Closed, selectionType: SelectionType.QuickView, selectionKey: '0' });
      }

      const openModalIndexFromPopState = getHashVariable('open-modal');
      if (openModalIndexFromPopState) {
        if (+openModalIndexFromPopState === 0) {
          setModal({
            toggleState: ModalToggleState.Closed,
          });
        } else {
          reverseModal();
        }
      } else {
        setModal({
          toggleState: ModalToggleState.Closed,
        });
      }
      const handle = document.location.pathname.replace('/products/', '') as ProductHandle;
      if (handle !== product?.handle && products[handle]) {
        initializeHandle(handle);
      }

      return true;
    };
  }
};

export default App;
